/**
 * 🟥 CUADRO NEGRO V6 — PullRequestsPanel.
 *
 * Modal flotante que muestra los PRs del repo y permite por cada uno:
 *   - Ver en GitHub.
 *   - Generar review con IA (POST /api/github/review-pr).
 *   - Publicar review en GitHub (POST /api/github/post-review).
 *   - Mergear (POST /api/github/merge-pr).
 *
 * También permite crear un PR nuevo si el branch actual no es el default.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  GitPullRequest, X, Loader2, ExternalLink, Sparkles, GitMerge,
  Send, Plus, ChevronDown, ChevronRight, AlertCircle, AlertTriangle, Info,
  Wand2, Repeat,
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';
import AutoFixResultModal from './AutoFixResultModal';
import AutoFixLoopResultModal from './AutoFixLoopResultModal';

const API = `${BACKEND_URL}/api`;

const stateColor = {
  open: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  closed: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
  merged: 'bg-violet-500/10 text-violet-300 border-violet-500/30',
};

const sevIcon = {
  info: <Info className="w-3 h-3 text-sky-400" />,
  warn: <AlertTriangle className="w-3 h-3 text-amber-400" />,
  error: <AlertCircle className="w-3 h-3 text-rose-400" />,
};

export default function PullRequestsPanel({
  moduleId,
  githubSource,
  defaultBranch = 'main',
  isEs = true,
  onClose,
  onMerged,
}) {
  const [prs, setPrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState('open');
  const [expanded, setExpanded] = useState({}); // pr_number -> review data
  const [busy, setBusy] = useState(null); // `${pr_number}:action`

  // Create-PR modal state.
  const [showCreate, setShowCreate] = useState(false);
  const currentBranch = githubSource?.branch || '';
  const canCreate = currentBranch && currentBranch !== defaultBranch;

  // 🟥 V7.1 — Auto-Fix IA state.
  const [autoFixResult, setAutoFixResult] = useState(null);
  // 🟥 V7.3 — Auto-Fix Loop state.
  const [loopResult, setLoopResult] = useState(null);

  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newBase, setNewBase] = useState(defaultBranch);
  const [creating, setCreating] = useState(false);

  const fetchPRs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/github/prs`, {
        params: { module_id: moduleId, state: stateFilter },
      });
      setPrs(res.data?.prs || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `No se pudieron cargar PRs: ${detail}` : `PRs load failed: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const id = setTimeout(() => { fetchPRs(); }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, stateFilter]);

  const handleReview = async (pr) => {
    setBusy(`${pr.number}:review`);
    try {
      const res = await axios.post(`${API}/github/review-pr`, {
        module_id: moduleId,
        pr_number: pr.number,
      });
      setExpanded((cur) => ({ ...cur, [pr.number]: res.data }));
      const cnt = (res.data?.comments || []).length;
      toast.success(
        isEs ? `Review IA generada · ${cnt} comentarios` : `AI review ready · ${cnt} comments`
      );
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Review IA falló: ${detail}` : `AI review failed: ${detail}`);
    } finally {
      setBusy(null);
    }
  };

  const handlePostReview = async (pr) => {
    const data = expanded[pr.number];
    if (!data) return;
    setBusy(`${pr.number}:post`);
    try {
      const res = await axios.post(`${API}/github/post-review`, {
        module_id: moduleId,
        pr_number: pr.number,
        summary: data.summary || '',
        comments: data.comments || [],
        event: 'COMMENT',
      });
      toast.success(
        isEs
          ? `Review publicado · ${res.data?.posted_comments || 0} comentarios`
          : `Review posted · ${res.data?.posted_comments || 0} comments`
      );
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Publicación falló: ${detail}` : `Post failed: ${detail}`);
    } finally {
      setBusy(null);
    }
  };

  const handleAutoFix = async (pr) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      isEs
        ? `Auto-Fix IA aplicará cambios automáticamente al branch "${pr.from_branch}" y creará un commit. ¿Continuar?`
        : `AI Auto-Fix will apply changes to branch "${pr.from_branch}" and create a commit. Continue?`
    );
    if (!ok) return;
    setBusy(`${pr.number}:autofix`);
    try {
      const res = await axios.post(`${API}/github/auto-fix-pr`, {
        module_id: moduleId,
        pr_number: pr.number,
        dispatch_ci: true,
      });
      const applied = (res.data?.applied_fixes || []).length;
      const skipped = (res.data?.skipped || []).length;
      if (applied === 0) {
        toast.info(isEs ? 'No se encontraron fixes para aplicar.' : 'No fixes to apply.');
      } else {
        toast.success(
          isEs
            ? `Auto-Fix: ${applied} fixes aplicados · ${skipped} omitidos · PR actualizado`
            : `Auto-Fix: ${applied} applied · ${skipped} skipped · PR updated`
        );
      }
      setAutoFixResult(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      if (detail === 'pr_not_open') {
        toast.error(isEs ? 'El PR debe estar abierto' : 'PR must be open');
      } else if (detail === 'pr_has_no_changes') {
        toast.error(isEs ? 'El PR no tiene cambios' : 'PR has no changes');
      } else if (detail === 'pr_from_fork_not_supported') {
        toast.error(isEs ? 'Auto-Fix no soporta PRs desde forks' : 'Auto-Fix does not support forks');
      } else if (detail === 'autofix_llm_not_configured') {
        toast.error(isEs ? 'Auto-Fix no configurado (falta LLM key)' : 'Auto-Fix not configured');
      } else {
        toast.error(isEs ? `Auto-Fix falló: ${detail}` : `Auto-Fix failed: ${detail}`);
      }
    } finally {
      setBusy(null);
    }
  };

  const handleAutoFixLoop = async (pr) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      isEs
        ? `DamyxIA intentará arreglar este PR autónomamente hasta 3 veces: Auto-Fix → CI → leer logs → Auto-Fix → repetir.\n\nPuede tardar hasta ~7 minutos. ¿Continuar?`
        : `DamyxIA will autonomously fix this PR up to 3 times: Auto-Fix → CI → read logs → Auto-Fix → repeat.\n\nMay take up to ~7 minutes. Continue?`
    );
    if (!ok) return;
    setBusy(`${pr.number}:loop`);
    try {
      const res = await axios.post(`${API}/github/auto-fix-loop`, {
        module_id: moduleId,
        pr_number: pr.number,
        max_iterations: 3,
      }, { timeout: 600000 }); // 10 min timeout cliente

      const fs = res.data?.final_status;
      const its = (res.data?.iterations || []).length;
      if (fs === 'success') {
        toast.success(
          isEs ? `🎉 CI verde tras ${its} iteración(es) · PR listo` : `🎉 CI green after ${its} iteration(s)`
        );
      } else if (fs === 'no_workflow') {
        toast.info(isEs ? 'Fixes aplicados · sin workflow CI para verificar' : 'Fixes applied · no CI workflow');
      } else if (fs === 'no_fixes') {
        toast.info(isEs ? 'La IA no encontró fixes para aplicar' : 'AI found no fixes');
      } else {
        toast.error(
          isEs ? `CI sigue fallando tras ${its} iteración(es)` : `CI still failing after ${its} iter(s)`
        );
      }
      setLoopResult(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      if (detail === 'pr_not_open') {
        toast.error(isEs ? 'El PR debe estar abierto' : 'PR must be open');
      } else if (detail === 'pr_from_fork_not_supported') {
        toast.error(isEs ? 'Loop no soporta PRs desde forks' : 'Loop does not support forks');
      } else {
        toast.error(isEs ? `Loop falló: ${detail}` : `Loop failed: ${detail}`);
      }
    } finally {
      setBusy(null);
    }
  };


  const handleMerge = async (pr) => {
    // eslint-disable-next-line no-alert
    const method = window.prompt(
      isEs
        ? 'Método de merge: squash, merge o rebase'
        : 'Merge method: squash, merge or rebase',
      'squash'
    );
    if (!method) return;
    const m = method.toLowerCase().trim();
    if (!['squash', 'merge', 'rebase'].includes(m)) {
      toast.error(isEs ? 'Método inválido' : 'Invalid method');
      return;
    }
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      isEs
        ? `¿Mergear PR #${pr.number} (${m}) en ${pr.to_branch}?`
        : `Merge PR #${pr.number} (${m}) into ${pr.to_branch}?`
    );
    if (!ok) return;
    setBusy(`${pr.number}:merge`);
    try {
      const res = await axios.post(`${API}/github/merge-pr`, {
        module_id: moduleId,
        pr_number: pr.number,
        method: m,
      });
      if (res.data?.merged) {
        toast.success(
          isEs
            ? `PR #${pr.number} mergeado · Workspace en ${res.data?.refreshed_branch || pr.to_branch}`
            : `PR #${pr.number} merged · Workspace on ${res.data?.refreshed_branch || pr.to_branch}`
        );
        onMerged?.(res.data);
        fetchPRs();
      } else {
        toast.info(isEs ? 'No se mergeó' : 'Not merged');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      if (detail === 'pr_not_mergeable') {
        toast.error(isEs ? 'PR no mergeable (conflictos)' : 'PR not mergeable (conflicts)');
      } else {
        toast.error(isEs ? `Merge falló: ${detail}` : `Merge failed: ${detail}`);
      }
    } finally {
      setBusy(null);
    }
  };

  const handleCreatePR = async () => {
    if (!newTitle.trim()) {
      toast.error(isEs ? 'Título requerido' : 'Title required');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/github/create-pr`, {
        module_id: moduleId,
        title: newTitle.trim(),
        body: newBody.trim(),
        from_branch: currentBranch,
        to_branch: newBase.trim() || defaultBranch,
      });
      toast.success(
        isEs ? `PR #${res.data?.pr_number} creado` : `PR #${res.data?.pr_number} created`
      );
      setShowCreate(false);
      setNewTitle('');
      setNewBody('');
      fetchPRs();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `No se pudo crear PR: ${detail}` : `Create PR failed: ${detail}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[8500] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="pr-panel"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-4xl h-[88vh] bg-gradient-to-b from-slate-900 to-slate-950 border border-violet-500/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-gradient-to-r from-violet-900/40 to-indigo-900/30">
          <div className="flex items-center gap-2">
            <GitPullRequest className="w-5 h-5 text-violet-300" />
            <div>
              <h2 className="text-base font-bold text-white">
                {isEs ? 'Pull Requests' : 'Pull Requests'}
              </h2>
              <p className="text-[11px] text-violet-300/80">
                {githubSource ? `${githubSource.owner}/${githubSource.repo}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="flex items-center gap-0 mr-2 bg-slate-800 rounded-md p-0.5">
              {['open', 'closed', 'all'].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStateFilter(s)}
                  data-testid={`pr-filter-${s}`}
                  className={`px-2 py-1 text-[11px] rounded ${
                    stateFilter === s ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {s === 'open' ? (isEs ? 'Abiertos' : 'Open') : s === 'closed' ? (isEs ? 'Cerrados' : 'Closed') : (isEs ? 'Todos' : 'All')}
                </button>
              ))}
            </div>
            {canCreate && (
              <button
                type="button"
                onClick={() => setShowCreate((v) => !v)}
                data-testid="pr-create-toggle"
                className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-emerald-600 hover:bg-emerald-500 text-white"
                title={isEs ? `Crear PR desde ${currentBranch}` : `Create PR from ${currentBranch}`}
              >
                <Plus className="w-3.5 h-3.5" />
                {isEs ? 'Crear PR' : 'Create PR'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              data-testid="pr-panel-close"
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Create PR inline form */}
        {showCreate && canCreate && (
          <div className="border-b border-slate-800 bg-slate-900/60 p-4 space-y-2" data-testid="pr-create-form">
            <div className="text-[11px] text-slate-400">
              <span className="font-mono text-violet-300">{currentBranch}</span> → <span className="font-mono text-emerald-300">{newBase}</span>
            </div>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder={isEs ? 'Título del PR' : 'PR title'}
              className="w-full px-3 py-2 text-sm bg-slate-950 border border-slate-700 rounded-md text-white focus:border-violet-500 focus:outline-none"
              data-testid="pr-create-title"
              disabled={creating}
            />
            <textarea
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              placeholder={isEs ? 'Descripción (opcional)' : 'Description (optional)'}
              rows={3}
              className="w-full px-3 py-2 text-sm bg-slate-950 border border-slate-700 rounded-md text-white focus:border-violet-500 focus:outline-none resize-none"
              data-testid="pr-create-body"
              disabled={creating}
            />
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-slate-400">{isEs ? 'Base:' : 'Base:'}</label>
              <input
                type="text"
                value={newBase}
                onChange={(e) => setNewBase(e.target.value)}
                className="px-2 py-1 text-xs bg-slate-950 border border-slate-700 rounded text-slate-200 font-mono"
                disabled={creating}
              />
              <div className="flex-1" />
              <button
                type="button"
                onClick={handleCreatePR}
                disabled={creating || !newTitle.trim()}
                data-testid="pr-create-submit"
                className="px-3 py-1.5 text-xs rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-60 text-white flex items-center gap-1"
              >
                {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                {isEs ? 'Crear PR' : 'Create PR'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                disabled={creating}
                className="px-2 py-1.5 text-xs rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300"
              >
                {isEs ? 'Cancelar' : 'Cancel'}
              </button>
            </div>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : prs.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-12">
              {isEs ? 'No hay PRs en este filtro.' : 'No PRs match this filter.'}
            </div>
          ) : (
            prs.map((pr) => {
              const isOpen = expanded[pr.number] !== undefined;
              const review = expanded[pr.number];
              return (
                <div
                  key={pr.number}
                  className="rounded-lg border border-slate-800 bg-slate-900/60 hover:border-violet-500/40 transition-colors"
                  data-testid={`pr-card-${pr.number}`}
                >
                  <div className="flex items-start justify-between gap-2 p-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${stateColor[pr.state] || stateColor.open}`}>
                          #{pr.number} · {pr.state}
                        </span>
                        {pr.draft && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">draft</span>
                        )}
                        <h3 className="text-sm text-white truncate font-medium">{pr.title}</h3>
                      </div>
                      <div className="mt-1 text-[11px] text-slate-400 flex items-center gap-2 flex-wrap">
                        <span className="font-mono">{pr.from_branch} → {pr.to_branch}</span>
                        {pr.author && <span>· {pr.author}</span>}
                        {pr.updated_at && <span>· {new Date(pr.updated_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <a
                        href={pr.url}
                        target="_blank"
                        rel="noreferrer"
                        title={isEs ? 'Abrir en GitHub' : 'Open on GitHub'}
                        className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800"
                        data-testid={`pr-open-${pr.number}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      <button
                        type="button"
                        onClick={() => handleReview(pr)}
                        disabled={busy === `${pr.number}:review`}
                        data-testid={`pr-review-${pr.number}`}
                        title={isEs ? 'Generar Code Review con IA' : 'Generate AI Code Review'}
                        className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-indigo-600/80 hover:bg-indigo-500 text-white disabled:opacity-60"
                      >
                        {busy === `${pr.number}:review`
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Sparkles className="w-3 h-3" />}
                        {isEs ? 'Review IA' : 'AI Review'}
                      </button>
                      {pr.state === 'open' && !pr.merged && (
                        <button
                          type="button"
                          onClick={() => handleAutoFix(pr)}
                          disabled={busy === `${pr.number}:autofix`}
                          data-testid={`pr-autofix-${pr.number}`}
                          title={isEs ? 'Auto-Fix IA · 1 pasada + commit + CI' : 'AI Auto-Fix · 1 pass'}
                          className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-fuchsia-600 hover:bg-fuchsia-500 text-white disabled:opacity-60"
                        >
                          {busy === `${pr.number}:autofix`
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Wand2 className="w-3 h-3" />}
                          Auto-Fix
                        </button>
                      )}
                      {pr.state === 'open' && !pr.merged && (
                        <button
                          type="button"
                          onClick={() => handleAutoFixLoop(pr)}
                          disabled={busy === `${pr.number}:loop`}
                          data-testid={`pr-autofix-loop-${pr.number}`}
                          title={isEs ? 'Auto-Fix Loop · hasta 3 iteraciones autónomas con CI auto-sanable' : 'Auto-Fix Loop · up to 3 self-healing iterations'}
                          className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-500 hover:to-indigo-500 text-white disabled:opacity-60"
                        >
                          {busy === `${pr.number}:loop`
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Repeat className="w-3 h-3" />}
                          Loop
                        </button>
                      )}
                      {pr.state === 'open' && !pr.merged && (
                        <button
                          type="button"
                          onClick={() => handleMerge(pr)}
                          disabled={busy === `${pr.number}:merge`}
                          data-testid={`pr-merge-${pr.number}`}
                          title={isEs ? 'Mergear PR' : 'Merge PR'}
                          className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-60"
                        >
                          {busy === `${pr.number}:merge`
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <GitMerge className="w-3 h-3" />}
                          {isEs ? 'Merge' : 'Merge'}
                        </button>
                      )}
                      {review && (
                        <button
                          type="button"
                          onClick={() => setExpanded((cur) => {
                            const n = { ...cur };
                            if (cur[pr.number] !== undefined) delete n[pr.number];
                            return n;
                          })}
                          className="p-1 rounded-md text-slate-400 hover:text-white"
                          aria-label={isEs ? 'Ocultar review' : 'Hide review'}
                        >
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* AI Review block */}
                  {review && isOpen && (
                    <div className="border-t border-slate-800 px-3 py-3 bg-slate-950/50 space-y-2" data-testid={`pr-review-block-${pr.number}`}>
                      <div className="text-[11px] uppercase tracking-wider text-violet-400">
                        {isEs ? 'Resumen IA' : 'AI Summary'}
                      </div>
                      <p className="text-xs text-slate-200">{review.summary}</p>
                      {Array.isArray(review.comments) && review.comments.length > 0 ? (
                        <div className="space-y-1.5">
                          <div className="text-[11px] uppercase tracking-wider text-violet-400">
                            {isEs ? `Comentarios (${review.comments.length})` : `Comments (${review.comments.length})`}
                          </div>
                          {review.comments.map((c, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-2 rounded-md bg-slate-900 border border-slate-800">
                              <div className="pt-0.5">{sevIcon[c.severity] || sevIcon.info}</div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[11px] font-mono text-violet-300 truncate">
                                  {c.path}:{c.line}
                                </div>
                                <div className="text-xs text-slate-200">{c.comment}</div>
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => handlePostReview(pr)}
                            disabled={busy === `${pr.number}:post`}
                            data-testid={`pr-post-review-${pr.number}`}
                            className="mt-1 flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-md bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-60"
                          >
                            {busy === `${pr.number}:post`
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <Send className="w-3 h-3" />}
                            {isEs ? 'Publicar review en GitHub' : 'Post review to GitHub'}
                          </button>
                        </div>
                      ) : (
                        <div className="text-[11px] text-slate-500 italic">
                          {isEs ? 'Sin comentarios — todo se ve bien.' : 'No comments — looks good.'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <footer className="px-5 py-2.5 border-t border-slate-800 bg-slate-900/60">
          <p className="text-[11px] text-slate-500 italic text-center">
            {isEs
              ? 'Code Review con IA generado por DamyxIA · acepta o edita antes de publicar.'
              : 'AI-generated review by DamyxIA · accept or edit before posting.'}
          </p>
        </footer>
      </div>

      {/* 🟥 V7.1 — Modal de resultado Auto-Fix IA */}
      {autoFixResult && (
        <AutoFixResultModal
          result={autoFixResult}
          isEs={isEs}
          onClose={() => setAutoFixResult(null)}
        />
      )}

      {/* 🟥 V7.3 — Modal de resultado Auto-Fix Loop */}
      {loopResult && (
        <AutoFixLoopResultModal
          result={loopResult}
          isEs={isEs}
          onClose={() => setLoopResult(null)}
        />
      )}

    </div>
  );
}
